// frontend/src/locales/languages.ts
// Language configuration for the live app.

export interface LanguageConfig {
  code: string;
  name: string;
  nativeName: string;
  flag?: string;
  hasStaticTranslation: boolean;
}

// Keep only English and French live for this refonte. A future i18n layer can
// reintroduce auto-translation deliberately, without exposing stale locale files.
export const SUPPORTED_LANGUAGES: LanguageConfig[] = [
  { code: 'en', name: 'English', nativeName: 'English', hasStaticTranslation: true },
  { code: 'fr', name: 'French', nativeName: 'Français', hasStaticTranslation: true },
];

export const DEFAULT_LANGUAGE = 'en';

export const normalizeLanguageCode = (code: unknown): string | null => {
  if (typeof code !== 'string') return null;

  const normalized = code.trim().toLowerCase();
  if (!/^[a-z]{2}(?:-[a-z]{2})?$/.test(normalized)) return null;

  const baseCode = normalized.split('-')[0];
  return SUPPORTED_LANGUAGES.some((language) => language.code === baseCode)
    ? baseCode
    : null;
};

export const isSupportedLanguageCode = (code: unknown): boolean => {
  return normalizeLanguageCode(code) !== null;
};

export const getLanguageByCode = (code: unknown): LanguageConfig | undefined => {
  const normalized = normalizeLanguageCode(code);
  return SUPPORTED_LANGUAGES.find((language) => language.code === normalized);
};

export const getLanguageName = (code: unknown): string => {
  const lang = getLanguageByCode(code);
  return lang?.nativeName || SUPPORTED_LANGUAGES[0].nativeName;
};

// The live language set is LTR only.
export const isRTL = (_code: string): boolean => {
  return false;
};

// Check if language has static translations
export const hasStaticTranslation = (code: unknown): boolean => {
  const lang = getLanguageByCode(code);
  return lang ? lang.hasStaticTranslation : false;
};
